import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NAV_LINKS } from "@/data/index";

// @section: header-nav
export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href: string) => {
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        backgroundColor: "#b3ebf2",
        boxShadow: scrolled ? "0 2px 16px rgba(30,45,78,0.15)" : "none",
      }}
    >
      {/* @section: header-inner */}
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">

          {/* Logo + Brand */}
          <button
            onClick={() => scrollTo("#inicio")}
            className="flex items-center gap-3 group"
          >
            <img
              src="/images/logo.jpg"
              alt="Logo Proyecto Nebrija"
              className="h-10 w-10 rounded-full object-contain bg-white p-0.5"
            />
            <div className="flex flex-col leading-tight">
              <span
                className="text-lg font-bold tracking-wide"
                style={{ fontFamily: "'Playfair Display', serif", color: "#1e2d4e" }}
              >
                Proyecto Nebrija
              </span>
              <span className="text-xs hidden md:block" style={{ color: "rgba(30,45,78,0.6)" }}>
                Lengua y Literatura · Formación docente
              </span>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.label}
                onClick={() => scrollTo(link.href)}
                className="px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200"
                style={{ color: "rgba(30,45,78,0.85)" }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.color = "#c9972a";
                  (e.target as HTMLElement).style.backgroundColor = "rgba(201,151,42,0.1)";
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.color = "rgba(30,45,78,0.85)";
                  (e.target as HTMLElement).style.backgroundColor = "transparent";
                }}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => scrollTo("#registro")}
              className="text-sm font-medium border border-transparent hover:border-accent/40"
              style={{ color: "rgba(30,45,78,0.85)" }}
            >
              Entrar
            </Button>
            <Button
              size="sm"
              onClick={() => scrollTo("#registro")}
              className="text-sm font-semibold"
              style={{ backgroundColor: "#c9972a", color: "#fff" }}
            >
              Registrarse
            </Button>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden p-2 rounded-md"
            style={{ color: "#1e2d4e" }}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menú"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div
            className="lg:hidden py-4 border-t"
            style={{ borderColor: "rgba(30,45,78,0.15)" }}
          >
            <nav className="flex flex-col gap-1 mb-4">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.label}
                  onClick={() => scrollTo(link.href)}
                  className="text-left px-3 py-2 text-sm font-medium rounded-md"
                  style={{ color: "rgba(30,45,78,0.85)" }}
                >
                  {link.label}
                </button>
              ))}
            </nav>
            <div className="flex gap-2 px-3">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 text-sm"
                style={{ borderColor: "rgba(30,45,78,0.3)", color: "#1e2d4e" }}
              >
                Entrar
              </Button>
              <Button
                size="sm"
                className="flex-1 text-sm font-semibold"
                style={{ backgroundColor: "#c9972a", color: "#fff" }}
              >
                Registrarse
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
